import React, { createContext, useContext, useState, useEffect } from 'react';
import { canAccessFeature } from '../utils/featureAccess';

const GuideContext = createContext(null);
const GUIDE_KEY = 'hasSeenStartGuide';

// Master definition of every section the guide can cover.
// Each entry produces up to 2 steps: a sidebar-highlight step and (optionally) a page-button step.
const SECTION_DEFS = [
  // ── المدرسة ───────────────────────────────────────────────────────────────
  {
    path: '/students',
    sidebarId: 'guide-students',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الطلاب',
    sidebarDesc: 'اضغط على "الطلاب" في القائمة الجانبية للوصول إلى قائمة الطلاب.',
    pageId: 'guide-add-student',
    navigateTo: '/students',
    pageTitle: 'إضافة طالب جديد',
    pageDesc: 'اضغط على زر "إضافة طالب" لإدخال بيانات طالب جديد في النظام.',
  },
  {
    path: '/classes',
    sidebarId: 'guide-classes',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الفصول الدراسية',
    sidebarDesc: 'اضغط على "الصفوف" لإنشاء الفصول الدراسية وتعيين الطلاب والمواد.',
    pageId: 'guide-add-class',
    navigateTo: '/classes',
    pageTitle: 'إضافة صف جديد',
    pageDesc: 'اضغط على زر "إضافة صف جديد" لإنشاء فصل دراسي جديد.',
  },
  {
    path: '/staff',
    sidebarId: 'guide-staff',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الموظفين',
    sidebarDesc: 'اضغط على "الموظفين" لإدارة بيانات الموظفين ورواتبهم.',
    pageId: 'guide-add-staff',
    navigateTo: '/staff',
    pageTitle: 'إضافة موظف جديد',
    pageDesc: 'اضغط على زر "إضافة موظف جديد" لإدخال بيانات موظف جديد.',
  },
  {
    path: '/buses',
    sidebarId: 'guide-buses',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الحافلات',
    sidebarDesc: 'اضغط على "الحافلات" لإدارة أسطول الحافلات المدرسية.',
    pageId: 'guide-add-bus',
    navigateTo: '/buses',
    pageTitle: 'إضافة حافلة جديدة',
    pageDesc: 'اضغط على زر "إضافة حافلة جديدة" لتسجيل حافلة مدرسية.',
  },
  {
    path: '/store',
    sidebarId: 'guide-store',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'المستودع',
    sidebarDesc: 'اضغط على "المستودع" لإدارة المخزون والمواد المدرسية وتتبع الكميات.',
  },
  {
    path: '/events',
    sidebarId: 'guide-events',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الفعاليات',
    sidebarDesc: 'اضغط على "الفعاليات" لإدارة الفعاليات والأنشطة المدرسية.',
    pageId: 'guide-add-event',
    navigateTo: '/events',
    pageTitle: 'إضافة فعالية جديدة',
    pageDesc: 'اضغط على زر "إضافة فعالية" لإنشاء فعالية مدرسية جديدة.',
  },
  {
    path: '/library',
    sidebarId: 'guide-library',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'المكتبة',
    sidebarDesc: 'اضغط على "المكتبة" لإدارة الكتب والمصادر وتسجيل الإعارات.',
  },
  {
    path: '/authorized-payers',
    sidebarId: 'guide-authorized-payers',
    sidebarSection: 'المدرسة',
    sidebarTitle: 'الموردون',
    sidebarDesc: 'اضغط على "الموردون" لإدارة الجهات المخوّلة بالدفع.',
  },
  // ── المالية ───────────────────────────────────────────────────────────────
  {
    path: '/financial',
    sidebarId: 'guide-financial',
    sidebarSection: 'المالية',
    sidebarTitle: 'سندات الصرف',
    sidebarDesc: 'اضغط على "إدارة سندات الصرف" لتسجيل ومتابعة مدفوعات المدرسة.',
    pageId: 'guide-add-financial',
    navigateTo: '/financial',
    pageTitle: 'إضافة سند صرف',
    pageDesc: 'اضغط على زر "إضافة سند صرف" لتسجيل دفعة مالية خارجية جديدة.',
  },
  {
    path: '/recipient',
    sidebarId: 'guide-recipient',
    sidebarSection: 'المالية',
    sidebarTitle: 'سندات القبض',
    sidebarDesc: 'اضغط على "إدارة سندات القبض" لتسجيل المدفوعات الواردة.',
    pageId: 'guide-add-recipient',
    navigateTo: '/recipient',
    pageTitle: 'إضافة سند قبض',
    pageDesc: 'اضغط على زر "إضافة سند قبض" لتسجيل دفعة واردة من طالب أو جهة.',
  },
  {
    path: '/finance/cheques-status',
    sidebarId: 'guide-cheques',
    sidebarSection: 'المالية',
    sidebarTitle: 'دفعات الطلاب',
    sidebarDesc: 'اضغط على "دفعات الطلاب" لمتابعة حالة الشيكات والأقساط.',
  },
  {
    path: '/fee-management',
    sidebarId: 'guide-fee-management',
    sidebarSection: 'المالية',
    sidebarTitle: 'إدارة الرسوم',
    sidebarDesc: 'اضغط على "إدارة الرسوم" لتحديد الرسوم الدراسية لكل صف أو طالب.',
  },
  // ── إداري ─────────────────────────────────────────────────────────────────
  {
    path: '/admin/users',
    sidebarId: 'guide-admin-users',
    sidebarSection: 'إداري',
    sidebarTitle: 'إدارة المستخدمين',
    sidebarDesc: 'اضغط على "إدارة المستخدمين" لإنشاء حسابات المعلمين والموظفين وضبط الصلاحيات.',
  },
  {
    path: '/reports',
    sidebarId: 'guide-reports',
    sidebarSection: 'إداري',
    sidebarTitle: 'التقارير',
    sidebarDesc: 'اضغط على "تقارير" للاطلاع على التقارير الإحصائية والمالية للمدرسة.',
  },
  {
    path: '/settings',
    sidebarId: 'guide-settings',
    sidebarSection: 'إداري',
    sidebarTitle: 'الإعدادات',
    sidebarDesc: 'اضغط على "الإعدادات" لتخصيص النظام وإدارة معلومات المدرسة.',
  },
  {
    path: '/admin/year-transition',
    sidebarId: 'guide-year-transition',
    sidebarSection: 'إداري',
    sidebarTitle: 'انتقال السنة الدراسية',
    sidebarDesc: 'اضغط على "انتقال السنة الدراسية" لإجراء عملية الترفيع في نهاية كل عام.',
  },
];

const buildSteps = () => {
  const steps = [
    {
      id: 'welcome',
      target: null,
      section: null,
      tooltipSide: null,
      navigateTo: null,
      title: 'مرحباً بك في النظام!',
      description: 'دليل البدء السريع سيساعدك على التعرف على جميع أقسام النظام المتاحة لك. يمكنك تخطيه في أي وقت.',
    },
  ];

  for (const def of SECTION_DEFS) {
    if (!canAccessFeature(def.path)) continue;

    // Sidebar-highlight step
    steps.push({
      id: `${def.sidebarId}-sidebar`,
      target: def.sidebarId,
      section: def.sidebarSection,
      tooltipSide: 'left',
      navigateTo: null,
      title: def.sidebarTitle,
      description: def.sidebarDesc,
    });

    // Page-button step (only if the section def declares a pageId)
    if (def.pageId) {
      steps.push({
        id: `${def.pageId}-page`,
        target: def.pageId,
        section: null,
        tooltipSide: 'bottom',
        navigateTo: def.navigateTo,
        title: def.pageTitle,
        description: def.pageDesc,
      });
    }
  }

  steps.push({
    id: 'done',
    target: null,
    section: null,
    tooltipSide: null,
    navigateTo: null,
    title: 'أنت جاهز الآن!',
    description: 'لقد تعرفت على جميع أقسام النظام المتاحة لك. يمكنك البدء باستخدامه بكل حرية.',
  });

  return steps;
};

export const GuideProvider = ({ children }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [active, setActive] = useState(false);
  const [steps] = useState(() => buildSteps());

  useEffect(() => {
    if (!localStorage.getItem(GUIDE_KEY)) {
      setActive(true);
    }
  }, []);

  const currentStep = active ? (steps[stepIndex] ?? null) : null;

  const nextStep = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex((s) => s + 1);
    } else {
      dismiss();
    }
  };

  const prevStep = () => setStepIndex((s) => Math.max(0, s - 1));

  const dismiss = () => {
    localStorage.setItem(GUIDE_KEY, 'true');
    setActive(false);
  };

  return (
    <GuideContext.Provider value={{
      active,
      currentStep,
      stepIndex,
      totalSteps: steps.length,
      nextStep,
      prevStep,
      dismiss,
    }}>
      {children}
    </GuideContext.Provider>
  );
};

export const useGuide = () => useContext(GuideContext);
